// Settings > Account and backup: sign in, cloud backup, and the ways this account can sign in.
//
// Sign-in is optional. Signed out, Re:Vault works exactly as before (local-only). Signed in, the
// same save blob the local backup already uses can be pushed to and pulled from the cloud, so a
// student's setup follows them across devices and school computers.
//
// Signing in and adding a way to sign in both open the sheet in the Re:Vault window (signIn() asks
// the top window); this page only lists, renames and removes methods through our own server.

import { leftOffMessage } from "../../shared/consent.js";
import {
	accountConfig, getSession, signIn, signOut, refreshProfile,
	syncUp, syncDown, onAccountChange, authedFetch, getStanding, setHandle,
} from "../../app/account.js";
import { offered, joinOr } from "../../app/signin-core.js";
import { inlineConfirm } from "../inline-confirm.js";
import { usernameForm } from "./username.js";
import { usernameLine } from "../../shared/username-line.js";
import { signOutCheck, loadEngine } from "./study-sync.js";
import { clearSync } from "../../shared/sync-state.js";

// Signing out with study sync on: everything already in the account can come off this device, so
// the next student on a shared Chromebook can't add it to theirs. Ticked only when nothing is
// waiting to sync. The sync state goes either way, all but the account's reset times.
export const SIGNOUT_REMOVE = "Also remove my study work from this device. It's saved in your account.";
export const SIGNOUT_UNSYNCED = "Some changes haven't synced yet, so they stay on this device.";
async function leave(removeWork) {
	if (removeWork) { try { (await loadEngine()).forget(true); } catch { /* sign out anyway */ } }
	clearSync();
	signOut();
}

// "Sign out everywhere": the server refuses every token this account holds now (all devices), then
// this device forgets its own. A 401 means it was already signed out, which is the goal.
async function signOutEverywhere() {
	let res;
	try { res = await authedFetch("/account/signout-all", { method: "POST" }); }
	catch (e) { if (!getSession()) return true; throw e; }
	if (!res.ok) throw new Error("Couldn't sign out everywhere. Try again.");
	clearSync();
	signOut();
	return true;
}

function btn(label, onClick, cls) {
	const b = document.createElement("button");
	b.type = "button";
	b.className = "rv-btn" + (cls ? " " + cls : "");
	b.textContent = label;
	b.addEventListener("click", onClick);
	return b;
}
function el(tag, cls, text) { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; }
function fmtWhen(ms) { return ms ? new Date(ms).toLocaleString() : "never"; }

const TYPE_LABEL = { google: "Google", microsoft: "Microsoft", discord: "Discord", email: "Email", passkey: "Passkey", key: "Account key" };
// Recent activity: sign-ins by method, and the account key's own events by kind.
export const EVENT_LABEL = {
	google: "Google", microsoft: "Microsoft", discord: "Discord", ep3: "Discord (Ep3 code)", email: "Email link", passkey: "Passkey", key: "Account key",
	key_add: "Made an account key",
	key_regen: "Made a new account key",
	key_remove: "Removed the account key",
	key_paused: "Account key paused after wrong PINs",
	key_off: "Account key turned off after wrong PINs",
};
export const KEY_COPY = {
	make: "Make an account key",
	remake: "Make a new key",
	nudge: "Keys can get lost. Add Discord or a passkey too, so you can always get back in.",
	nudgeBtn: "Add Discord or a passkey",
	loadFailed: "The account key screen couldn't load. Check your connection and try again.",
	switchedOff: "Account keys are turned off right now.",
};
const clock = (sec) => new Date(sec * 1000).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

// "today", "yesterday", "3 days ago", or a date. `sec` is unix seconds.
export function ago(sec, now = Date.now()) {
	if (!sec) return "";
	const d = new Date(sec * 1000), n = new Date(now);
	const days = Math.round((new Date(n.getFullYear(), n.getMonth(), n.getDate()) - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000);
	if (days <= 0) return "today";
	if (days === 1) return "yesterday";
	if (days < 7) return days + " days ago";
	return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
// The second line of a method row. keysOn is false while account keys are switched off for
// everyone (the auth service doesn't list "key").
export function methodDetail(m, now = Date.now(), keysOn = true) {
	const parts = [];
	if (m.type === "key") {
		if (!keysOn) return KEY_COPY.switchedOff;
		if (m.state === "off") return "turned off after too many wrong PINs. Make a new key to use it again.";
		if (m.state === "paused" && m.pausedUntil * 1000 > now) return "paused until " + clock(m.pausedUntil) + " after wrong PINs";
	}
	if (m.type === "passkey") {
		if (m.locked) parts.push("turned off: it looked copied");
		else if (m.synced) parts.push("synced");
	}
	if (m.lastUsedAt) parts.push("last used " + ago(m.lastUsedAt, now));
	else if (m.createdAt) parts.push("added " + ago(m.createdAt, now));
	return parts.join(" · ");
}

// The key save screen is the only time the code shows, so a session update (a token renewal, a
// profile refresh) must leave it open. It goes on sign-out or when a different account signs in.
export function keyPanelStays(openSub, sess) {
	if (!sess) return false;
	const sub = (sess.profile && sess.profile.sub) || "";
	return !openSub || !sub || sub === openSub;
}

export function accountBlock() {
	const body = el("div", "rv-account-wrap");
	// Where the account key screens draw. One element for the life of this section, so they
	// survive a re-render of everything around them.
	const keyHost = el("div", "rv-key-host");

	// A live region we re-render whenever sign-in state changes.
	const live = el("div", "rv-account");
	body.append(live);

	let gen = 0;
	async function render() {
		const my = ++gen;
		// Signed out or switched account: the key screens (and any code on them) go too. Anything
		// else (a renewed token, a refreshed profile) leaves them open.
		if (keyPanel && !keyPanelStays(keyPanelSub, getSession())) keyPanel.close();
		const cfg = await accountConfig();
		if (my !== gen) return;
		live.textContent = "";
		if (!cfg.enabled) {
			if (keyPanel) keyPanel.close();
			live.append(el("p", "rv-account-note", "Cloud accounts aren’t enabled on this server yet."));
			return;
		}
		const sess = getSession();
		if (!sess) {
			if (keyPanel) keyPanel.close();
			live.append(el("p", "rv-account-note", "Sign in to back up your study work, settings, favorites, history and looks to the cloud, and carry them between devices. It’s optional: everything works signed out."));
			const ways = offered(cfg.methods);
			if (ways.length > 1) live.append(el("p", "rv-account-note", "Use " + joinOr(ways) + ", whichever your school lets you."));
			const g = btn("Sign in", () => { signIn().catch(() => {}); }, "rv-btn-primary");
			live.append(g);
			return;
		}

		// Signed in.
		const p = sess.profile || {};
		const who = el("div", "rv-account-who");
		const av = el("div", "rv-account-av");
		av.textContent = (p.name || "?").charAt(0).toUpperCase();
		const meta = el("div", "rv-account-meta");
		meta.append(el("div", "rv-account-name", p.name || "Signed in"));
		who.append(av, meta);
		live.append(who);
		// One load of the public username feeds both the line at the top and the form below it.
		const standing = getStanding();
		standing.catch(() => {});
		const form = usernameForm({ getSession, getStanding: () => standing, setHandle, onSaved: (h) => line.set(h) });
		const line = usernameLine(standing, { onPick: () => { const i = form.querySelector("input"); i.scrollIntoView({ block: "center" }); i.focus(); } });
		meta.append(line);
		live.append(form);

		const backupInfo = el("p", "rv-account-note", "Last cloud backup: " + fmtWhen(sess.backupAt));
		live.append(backupInfo);

		const rowEl = el("div", "rv-account-actions");
		const up = btn("Back up to cloud", async () => {
			up.disabled = true; up.textContent = "Backing up…";
			try { const r = await syncUp(); await refreshProfile(); backupInfo.textContent = "Last cloud backup: " + fmtWhen(r.updatedAt || Date.now()); toast("Backed up to the cloud."); }
			catch (e) { toast(e.message || "Backup failed."); }
			up.disabled = false; up.textContent = "Back up to cloud";
		}, "rv-btn-primary");
		const down = btn("Restore from cloud", async () => {
			if (!confirm("Bring back your cloud backup? Anything in it replaces the same item on this device, then Re:Vault reloads.")) return;
			down.disabled = true; down.textContent = "Restoring…";
			try { const r = await syncDown(); const left = leftOffMessage(r.leftOff); if (left) alert(left); toast("Restored. Reloading…"); setTimeout(() => location.reload(), 700); }
			catch (e) { toast(e.message || "Restore failed."); down.disabled = false; down.textContent = "Restore from cloud"; }
		});
		const out = btn("Sign out", async () => {
			out.disabled = true;
			const chk = await signOutCheck();
			out.disabled = false;
			let cb = null;
			const c = ask(out, rowEl, {
				text: "Sign out of this device? Your cloud backup stays safe.",
				okLabel: "Sign out",
				onOk: () => leave(!!(cb && cb.checked)),
			});
			if (!chk) return;
			const lab = el("label", "rv-way-check");
			cb = el("input"); cb.type = "checkbox"; cb.checked = chk.synced;
			lab.append(cb, document.createTextNode(" " + SIGNOUT_REMOVE));
			c.box.insertBefore(lab, c.box.lastChild);
			if (!chk.synced) c.box.insertBefore(el("p", "rv-account-note", SIGNOUT_UNSYNCED), lab);
		}, "rv-btn-danger");
		rowEl.append(up, down, out);
		live.append(rowEl);

		live.append(waysBlock(), signinsBlock());

		// Ends every session this account has, on every device, including one copied off this
		// computer. Sign-in itself still works afterwards.
		const allNote = el("p", "rv-account-note", "Signed in somewhere you don't recognise, or think someone copied your sign-in? Sign out everywhere ends every session on every device. You can sign in again right after.");
		const all = btn("Sign out everywhere", () => ask(all, all, {
			text: "Sign out on every device, including this one?",
			okLabel: "Sign out everywhere",
			onOk: async (go) => {
				go.disabled = true; go.textContent = "Signing out…";
				try { await signOutEverywhere(); toast("Signed out everywhere."); }
				catch (e) { toast(e.message || "That didn't work. Try again."); go.disabled = false; go.textContent = "Sign out everywhere"; }
			},
		}), "rv-btn-danger");
		live.append(allNote, all, deleteBlock());
	}

	// Swaps a button for an in-page confirm placed after `where`; Cancel or Esc brings it back.
	function ask(opener, where, opts) {
		const c = inlineConfirm({ opener, ...opts });
		opener.hidden = true;
		where.after(c.box);
		c.go.focus();
		return c;
	}

	// --- Ways to sign in ----------------------------------------------------------------------
	let reloadWays = () => {};
	function waysBlock() {
		const wrap = el("div", "rv-ways");
		wrap.append(el("h3", "rv-ways-h", "Ways to sign in"));
		const list = el("div", "rv-ways-list");
		list.setAttribute("aria-live", "polite");
		const foot = el("div", "rv-ways-foot");
		// The account key screens: outside list and foot, and kept across re-renders, so a reload of
		// the list or a session update leaves them be.
		wrap.append(list, foot, keyHost);
		async function load() {
			list.textContent = ""; foot.textContent = "";
			list.append(el("p", "rv-account-note", "Loading…"));
			let j = null;
			try { const r = await authedFetch("/account/methods"); j = await r.json().catch(() => null); if (!r.ok) throw new Error((j && j.reason) || "Couldn't load your ways to sign in."); }
			catch (e) { list.textContent = ""; if (getSession()) list.append(el("p", "rv-account-note", e.message || "Couldn't load your ways to sign in.")); return; }
			list.textContent = "";
			const methods = Array.isArray(j.methods) ? j.methods : [];
			const keysOn = Array.isArray(j.enabled) && j.enabled.includes("key");
			for (const m of methods) list.append(methodRow(m, methods.length, load, keysOn));
			// What could still be added: a passkey any time, the others once each. An account key is
			// made here in Settings; the rest are added through the sign-in sheet.
			const have = new Set(methods.map((m) => m.type));
			const addable = offered(j.enabled).filter((t) => t === "passkey" || !have.has(t));
			const linkable = addable.filter((t) => t !== "key");
			if (j.linking && linkable.length) foot.append(btn("+ Add a way to sign in", () => { signIn({ intent: "link" }).catch(() => {}); }));
			if (j.linking && addable.includes("key")) foot.append(btn(KEY_COPY.make, () => openKey(false)));
			const others = addable.filter((t) => t !== "google");
			// Only a key: it can get lost, so point at Discord or a passkey.
			if (j.linking && have.has("key") && !have.has("discord") && !have.has("passkey")) {
				foot.append(el("p", "rv-account-note rv-ways-nudge", KEY_COPY.nudge));
				if (linkable.includes("discord") || linkable.includes("passkey")) foot.append(btn(KEY_COPY.nudgeBtn, () => { signIn({ intent: "link" }).catch(() => {}); }));
			} else if (j.linking && methods.length === 1 && others.length) {
				const words = { discord: "Discord", email: "an email", passkey: "a passkey", key: "an account key" };
				foot.append(el("p", "rv-account-note rv-ways-nudge", "You only have one way to sign in. School accounts are often deleted when you leave; add " + joinOr(others, words) + " so you don't lose your saves."));
			}
		}
		reloadWays = load;
		load();
		return wrap;
	}
	// The make/replace screens are a lazy chunk with the QR code library in it.
	let keyPanel = null;
	let keyPanelSub = "";
	function openKey(replace) {
		if (keyPanel) return;
		const loading = { close() { keyPanel = null; } };
		keyPanel = loading;
		const s = getSession();
		keyPanelSub = (s && s.profile && s.profile.sub) || "";
		import(__RVM.accountKeyJs).then((mod) => {
			if (keyPanel !== loading) return; // closed while it loaded
			keyPanel = mod.start({ host: keyHost, authedFetch, replace, onDone: (made) => { keyPanel = null; if (made) toast("Your account key is ready."); if (getSession()) reloadWays(); } });
		}, () => { keyPanel = null; toast(KEY_COPY.loadFailed); });
	}
	function methodRow(m, count, reload, keysOn) {
		const r = el("div", "rv-way");
		r.dataset.type = m.type;
		const text = el("div", "rv-way-text");
		const name = m.type === "passkey" ? (m.label || "Passkey") : (TYPE_LABEL[m.type] || m.type);
		text.append(el("div", "rv-way-name", name));
		const hint = m.type === "passkey" ? "Passkey" : (m.hint || "");
		const detail = [hint && hint !== name ? hint : "", methodDetail(m, Date.now(), keysOn)].filter(Boolean).join(" · ");
		if (detail) text.append(el("div", "rv-way-detail", detail));
		const acts = el("div", "rv-way-acts");
		if (m.type === "passkey" && m.id) {
			acts.append(btn("Rename", async () => {
				const label = prompt("Name this passkey (for example \"School Chromebook\")", m.label || "");
				if (label == null || !label.trim()) return;
				try {
					const res = await authedFetch("/account/methods/rename", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: m.id, label: label.trim().slice(0, 40) }) });
					if (!res.ok) throw new Error("Couldn't rename it. Try again.");
					reload();
				} catch (e) { toast(e.message || "Couldn't rename it. Try again."); }
			}));
		}
		if (m.type === "key" && m.id && keysOn) acts.append(btn(KEY_COPY.remake, () => openKey(true)));
		// A key turned off after wrong PINs can always go, even when it's the last way in.
		if (m.id && (count > 1 || (m.type === "key" && m.state === "off"))) {
			acts.append(btn("Remove", () => confirmRemove(r, m, name, reload), "rv-btn-danger"));
		}
		r.append(text, acts);
		return r;
	}
	// Removing a method signs out everywhere by default: people often remove one because they
	// think someone else used it.
	function confirmRemove(rowEl, m, name, reload) {
		if (rowEl.querySelector(".rv-way-confirm")) return;
		const box = el("div", "rv-way-confirm");
		box.append(el("p", "rv-account-note", "Remove " + name + " from this account? You won't be able to sign in with it any more."));
		const lab = el("label", "rv-way-check");
		const cb = el("input"); cb.type = "checkbox"; cb.checked = true;
		lab.append(cb, document.createTextNode(" Also sign out on every device"));
		const go = btn("Remove", async () => {
			go.disabled = true;
			try {
				const res = await authedFetch("/account/methods/remove", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: m.id, signOutEverywhere: cb.checked }) });
				const j = await res.json().catch(() => ({}));
				if (!res.ok) throw new Error(j.reason || "Couldn't remove it. Try again.");
				if (j.signedOut) { signOut(); toast("Removed. You're signed out everywhere; sign in again with another way."); return; }
				toast("Removed.");
				reload();
			} catch (e) { go.disabled = false; toast(e.message || "Couldn't remove it. Try again."); }
		}, "rv-btn-danger");
		const no = btn("Cancel", () => box.remove());
		const acts = el("div", "rv-account-actions");
		acts.append(go, no);
		box.append(lab, acts);
		rowEl.append(box);
	}

	// --- Recent sign-ins ---------------------------------------------------------------------
	function signinsBlock() {
		const wrap = el("div", "rv-ways");
		wrap.append(el("h3", "rv-ways-h", "Recent sign-ins"));
		const list = el("div", "rv-ways-list");
		wrap.append(list);
		authedFetch("/account/signins").then((r) => (r.ok ? r.json() : { events: [] })).then((j) => {
			const evs = (Array.isArray(j.events) ? j.events : []).filter((e) => e.kind === "signin" || /^key_/.test(e.kind)).slice(0, 5);
			if (!evs.length) { wrap.hidden = true; return; }
			for (const e of evs) {
				const r = el("div", "rv-way rv-way-ev");
				r.append(el("div", "rv-way-name", EVENT_LABEL[e.kind === "signin" ? e.method : e.kind] || e.method), el("div", "rv-way-detail", new Date(e.at * 1000).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })));
				list.append(r);
			}
		}).catch(() => { wrap.hidden = true; });
		return wrap;
	}

	// --- Delete account ----------------------------------------------------------------------
	function deleteBlock() {
		const wrap = el("div", "rv-ways");
		const open = btn("Delete account", () => {
			open.hidden = true;
			const box = el("div", "rv-way-confirm");
			box.append(el("p", "rv-account-note", "This deletes your cloud backup, any synced study copy, ratings and public name, removes every way to sign in, and takes your name off your forum and Study Hall posts. It can't be undone. Download a backup first if you want to keep your setup."));
			const input = el("input", "rv-input");
			input.type = "text"; input.placeholder = "Type delete to confirm"; input.setAttribute("aria-label", "Type delete to confirm");
			const go = btn("Delete my account", async () => {
				go.disabled = true;
				try {
					const res = await authedFetch("/account/delete", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ confirm: input.value }) });
					const j = await res.json().catch(() => ({}));
					if (!res.ok) throw new Error(j.reason || "Couldn't delete the account. Try again.");
					clearSync({ keep: false });
					signOut();
					toast("Your account is deleted.");
				} catch (e) { go.disabled = false; toast(e.message || "Couldn't delete the account. Try again."); }
			}, "rv-btn-danger");
			go.disabled = true;
			input.addEventListener("input", () => { go.disabled = input.value.trim().toLowerCase() !== "delete"; });
			const acts = el("div", "rv-account-actions");
			acts.append(go, btn("Cancel", () => { box.remove(); open.hidden = false; }));
			box.append(input, acts);
			wrap.append(box);
			input.focus();
		}, "rv-btn-danger");
		wrap.append(open);
		return wrap;
	}

	// A very small toast, scoped to this section.
	let toastEl = null, toastTimer = 0;
	function toast(msg) {
		if (!toastEl) { toastEl = el("div", "rv-account-toast"); toastEl.setAttribute("role", "status"); body.appendChild(toastEl); }
		toastEl.textContent = msg; toastEl.hidden = false;
		clearTimeout(toastTimer); toastTimer = setTimeout(() => { toastEl.hidden = true; }, 3000);
	}

	onAccountChange(() => render());
	// The Re:Vault window added a way to sign in (Discord through Ep3 or a Re:Vault tab).
	window.addEventListener("storage", (e) => { if (e.key === "revault:signin-changed" && getSession()) reloadWays(); });
	render();
	return body;
}
